import type { ReactNode } from "react";
import { useContext, useEffect, useMemo, useState } from "react";
import { AuthContext } from "./auth-context";
import { DotcomAuthContext } from "../context";
import type { DotcomAuthSession } from "../types";
import { enhanceUserData } from "./enhance-user-data";
import type { AuthState } from "@jetblue/kit-auth";

export { AuthProvider } from "./auth-provider";
export { AuthContext } from "./auth-context";
export { enhanceUserData } from "./enhance-user-data";
export const LegacyAuthBridge = ({ children }: { children: ReactNode }) => {
  const {
    IS_TOKEN_STORED,
    PROFILE,
    IS_CREDIT_PROFILE_USER,
    TOKEN,
    IS_B6AUTH_STORED,
    ERROR,
  } = useContext(AuthContext);
  const customerDetails = PROFILE?.customerDetails;
  const isCreditProfile = Boolean(IS_CREDIT_PROFILE_USER);
  const [accessToken, setAccessToken] = useState<string>();

  useEffect(() => {
    const tokenObject = TOKEN?.token;
    const tokenType = TOKEN?.tokenType;
    const newAccessToken =
      tokenObject && "accessToken" in tokenObject
        ? tokenObject.accessToken
        : undefined;

    if (tokenType === "accessToken" && newAccessToken !== accessToken) {
      setAccessToken(newAccessToken);
    }
  }, [TOKEN, accessToken]);

  const value = useMemo<DotcomAuthSession>(() => {
    const getAuthState = (): AuthState => {
      if (ERROR) {
        return "unauthenticated";
      }
      if (IS_B6AUTH_STORED === undefined) {
        return "authenticating";
      }
      if (IS_B6AUTH_STORED && !customerDetails) {
        return "authenticating";
      }
      if (customerDetails && IS_TOKEN_STORED) {
        return "authenticated";
      }
      return "unauthenticated";
    };
    const status = getAuthState();

    return {
      accessToken,
      customerDetails:
        status === "authenticated" && customerDetails
          ? enhanceUserData({ customerDetails, isCreditProfile })
          : undefined,
      error: null,
      status,
    };
  }, [accessToken, IS_B6AUTH_STORED, customerDetails, isCreditProfile, ERROR]);

  return (
    <DotcomAuthContext.Provider value={value}>
      {children}
    </DotcomAuthContext.Provider>
  );
};
